import { useQuery } from "@apollo/client"
import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState, useRecoilState } from "recoil"
import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import DateRangePicker from "../FromConnect/DateRangePicker"
import CommonTable from "../FromConnect/CommonTable/CommonTable"
import {
  projectIdState,
  projectOriginState,
  quadriProjectIdState,
  sidebarTaskState,
  usersByTidUuidState,
  selectedTaskRow,
  signedInUserState,
  projectNameState,
  OwnershipNameState
} from "../StateSelectors"
import {
  GET_ALL_TASKS,
  GET_GLOBAL_SETTINGS,
  GET_ROOTTASKS,
  GET_SUBTASKS,
  HAS_ACCESS,
  HAS_PROJECT_ACCESS
} from "../../api/QuadriApi"
import { PendingSpinner } from "../Pending"
import NoData from "../NoData/NoData"
import { formatDateAsConnect, formatDateOnlyAsConnect } from "../../util/dateTools"
import { iconBindings } from "../../assets/icons/quadriWindows/iconBindings"
import { userFullName } from "../UserRow"
import { getFilterKey, getOnFilterChanged, loadFilter, saveFilter } from "../../util/filtering"
import TasksBreadCrumbs from "./TasksBreadCrumbs"
import { useHistory } from "react-router-dom"
import { TasksFilter } from "../../types"
import { useTranslation } from "react-i18next"
import MissingLicense from "../MissingLicense"
import ProjectFeatureCatalogLink from "../ProjectFeatureCatalogLink"
import { OopsLocalError } from "../OopsLocalError"
import Checkbox from "../../util/Checkbox"
import FilterIcon from "../FilterIcon"
import FilterSidebar from "../FilterSidebar"
import deleted_icon from "../../assets/icons/quadri/deleted.svg"
import dashboard_icon from "../../assets/icons/quadri/dashboard.svg"
import UserPicker from "../FromConnect/UserPicker"
import GetEnvironment from "../../environments"
import ChartDashboard from "./ChartDashboard"
import { prepareChartData } from "../../util/dataForVisualization"
import { exceptionFromApolloError } from "../../util/apolloError"
import { DropDownPicker } from "../../util/DropDownPicker"
import { TaskTypeDefinition, QuadriRestApi } from "../../api/QuadriRestApi"
import Oops from "../Oops/Oops"

const TASKS_FILTER_STORAGE_KEY = "tasksFilter"

interface Task {
  icon: any
  taskName: string
  guid: string
  taskTag: string
  subTasks: string
  createdDate: string
  latestModderName: string
  latestDateMod: string
  latestModderId: string
  deleted: boolean
  type?: string
}

enum SortDir {
  Ascending = 0,
  Descending = 1
}

function makeDatePersonEntry(theDate: string, thePerson: string, by: string, noData: string) {
  const formattedDate = formatDateOnlyAsConnect(theDate)
  const personEntry = thePerson ? by + thePerson : ""
  const returnValue = theDate === undefined ? noData : formattedDate + personEntry
  return returnValue
}

function unique<T>(array: T[]): T[] {
  return Array.from(new Set(array))
}

const TasksMain = () => {
  useEffect(() => {
    if (sidebarToggle && sidebarToggle.clickedTaskId > 0)
      setSelectedTask({
        taskName: sidebarToggle.taskName,
        taskId: sidebarToggle.taskId,
        guid: sidebarToggle.guid,
        taskTag: sidebarToggle.taskTag,
        subTaskIds: sidebarToggle.subTaskIds,
        clickedTaskId: -1,
        createdData: sidebarToggle.createdData,
        lastModifiedData: sidebarToggle.lastModifiedData,
        includeDeleted: sidebarToggle.includeDeleted
      })
  })

  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)
  const projectId = useRecoilValue(projectIdState)
  const [needsLicense, setNeedsLicense] = useState(true)
  const signedInUser = useRecoilValue(signedInUserState)
  const users = useRecoilValueLoadable(usersByTidUuidState)
  const [taskRowSelected, setTaskRowSelected] = useRecoilState(selectedTaskRow)
  const [taskTypes, setTaskTypes] = useState<TaskTypeDefinition[]>([])

  let loadingStatus = false // Keeps track of whether any of the http-requests in this render are loading
  let errorStatus = 0 // Keeps track of which, if any, of the http-requests in this render was the first that generated an error

  const filterKey = getFilterKey(TASKS_FILTER_STORAGE_KEY, projectId!)

  const [sortConfig, setSortConfig] = useState({ col: "taskName", type: SortDir.Ascending })
  const [filter, setFilter] = useState(loadFilter(filterKey, getDefaultFilter()) as TasksFilter)
  const [prevTask, setPrevTask] = useState(null as any)
  const [getTasksFromAPI, setGetTasksFromAPI] = useState(false)
  const [showChart, setShowChart] = useState(false)
  const [chartData, setChartData] = useState<(string | number)[][]>([])
  const projectName = useRecoilValue(projectNameState)
  const ownershipName = useRecoilValue(OwnershipNameState)
  const [hasProjectAccess, setHasProjectAccess] = useState<boolean>(false)
  const handleToggle = () => {
    setGetTasksFromAPI((prevState) => !prevState)
  }
  const handleVisualizationClick = () => {
    setShowChart((prevState) => !prevState)
  }
  useEffect(() => saveFilter(filterKey, filter), [filter, filterKey])

  // Get global settings from settings api and set the quadriConfigurationUiUrl
  const getGlobalSettingsQuery = useQuery(GET_GLOBAL_SETTINGS)
  let quadriConfigurationUiUrl
  if (getGlobalSettingsQuery.data) {
    quadriConfigurationUiUrl = getGlobalSettingsQuery.data.getGlobalSettings.quadriConfigurationFrontEndUrl
  }

  const projectAccessQuery = useQuery(HAS_PROJECT_ACCESS, {
    variables: {
      connectProjectId: projectId
    },
    fetchPolicy: "network-only" // ← Always fetch fresh
  })

  useEffect(() => {
    const hasAccess = projectAccessQuery.data?.hasProjectAccess.projectAccess
    if (hasAccess !== undefined) {
      setHasProjectAccess(hasAccess)
    }
  }, [projectAccessQuery.data, setHasProjectAccess])

  //Add query to get all tasks for a project
  const { loading, error, data } = useQuery(GET_ALL_TASKS, {
    variables: { quadriProjectId: quadriProjectId ?? "" },
    skip: !GetEnvironment().featureFlagUseRestApi
  })

  useEffect(() => {
    if (!loading && !error && data) {
      setChartData(prepareChartData(data.getAllTasksFromApi))
    }
  }, [loading, error, data])

  useEffect(() => {
    const fetchTaskSchemas = async () => {
      const restApi = new QuadriRestApi()
      try {
        await restApi.initialize()
        const taskSchemas = await restApi.getTaskSchemas()
        console.log("Task schemas: ", taskSchemas)
        setTaskTypes(taskSchemas)
      } catch (error) {
        console.error("Error fetching task schemas: ", error)
      }
    }

    fetchTaskSchemas()
  }, [])

  const { t } = useTranslation(["tasks", "common"])

  const cBy = t("runtimeTexts.by")
  const cNone = t("runtimeTexts.noData")

  const resetFilter = () => setFilter(getDefaultFilter())

  const columnsSet = [
    {
      label: "",
      field: "icon",
      isSort: false,
      colClass: "iconColWidth",
      fieldClass: "iconMarginWidth"
    },
    {
      label: t("tasksGrid.name"),
      field: "taskName",
      isSort: true,
      colClass: "col col-sm-4 col-md-4 col-lg-4 taskHeadingMarginChop",
      fieldClass: "col col-sm-5 col-md-4 col-lg-4 taskNamePaddingChop text-ellipsis"
    },
    {
      label: t("tasksGrid.subCount"),
      field: "subTasksCount",
      isSort: false,
      colClass: "col col-sm-3 col-md-2 col-lg-1 list-data",
      fieldClass: "col col-sm-3 col-md-2 col-lg-1 list-data"
    },
    {
      label: t("tasksGrid.latestModder"),
      field: "latestModderName",
      isSort: false,
      colClass: "col col-sm-5 col-md-4 col-lg-3 list-data",
      fieldClass: "col col-sm-5 col-md-4 col-lg-3 list-data"
    },
    {
      label: t("tasksGrid.latestModTime"),
      field: "latestDateMod",
      isSort: false,
      colClass: "col col-sm-5 col-md-4 col-lg-3 list-data",
      fieldClass: "col col-sm-5 col-md-4 col-lg-3 list-data"
    }
  ]

  const errMessages = [
    t("runtimeTexts.accessCheckFailed"),
    t("runtimeTexts.rootTasksNotRead"),
    t("runtimeTexts.userCollNotRead")
  ]

  const onSort = (col: string) => {
    setSortConfig({
      ...sortConfig,
      type: sortConfig.type === SortDir.Ascending ? SortDir.Descending : SortDir.Ascending
    })
    invalidateDetailsPane()
  }

  const setSelectedTask = useSetRecoilState(sidebarTaskState)
  const sidebarToggle = useRecoilValue(sidebarTaskState)

  // ************************** HAS_ACCESS *******************************
  const accessQuery = useQuery(HAS_ACCESS)
  useEffect(() => {
    if (!accessQuery.loading && accessQuery.data) {
      setNeedsLicense(!accessQuery.data.hasAccess?.access)
    }
  }, [accessQuery.loading, accessQuery.data])

  const _onFilterChange = getOnFilterChanged(setFilter)

  const onFilterChange = (event: any, source: string) => {
    _onFilterChange(event, source)
  }

  const onSearchEnter = (e: any) => {
    e.preventDefault()
    setFilter({ ...filter, nameSearch: e.target.value })
    invalidateDetailsPane()
  }

  // ************************ GET_ROOTTASKS / GET_TASKS *****************************
  const routeParams: any = useParams()
  const isRoot = !("subTaskId" in routeParams)
  const taskIds = [isRoot ? [] : routeParams["subTaskId"]] // This task will go into the page heading
  const subSub = [isRoot ? [] : routeParams["subSubTaskId"]] // This task and any sibling task(s) will go into the main grid
  const callName = isRoot ? GET_ROOTTASKS : GET_SUBTASKS
  // const input = isRoot
  //   ? { quadriProjectId, include_deleted: filter.includeDeleted }
  //   : { quadriProjectId, taskIds, include_deleted: filter.includeDeleted }
  const input = isRoot
    ? { quadriProjectId, include_deleted: filter.includeDeleted, useRestApi: getTasksFromAPI }
    : { quadriProjectId, taskIds, include_deleted: filter.includeDeleted, useRestApi: getTasksFromAPI }
  let entryPosition = -1 // Determines which task, if any, is selected in the main grid. Count from top of grid. Default -1 = No task selected.
  const {
    loading: rootLoading,
    error: rootError,
    data: rootData
  } = useQuery(callName, {
    fetchPolicy: "no-cache",
    variables: input,
    errorPolicy: "all",
    skip: !hasProjectAccess, // ← SKIP unless access is granted
    // Reset the sidebar that depends on data from this query
    onCompleted: () => setSelectedTask(null)
  })

  const history = useHistory()

  if (rootLoading) {
    loadingStatus = true
  } else if (rootError && errorStatus === 0 && !loadingStatus) {
    errorStatus = 2
  }
  // ******************** USERS (for filtering) *************************
  if (users.state === "loading") loadingStatus = true
  else if (users.state === "hasError") {
    errorStatus = 3
  }

  //Need to wait for the lisense check to finish before rendering the table
  if (accessQuery.loading || projectAccessQuery.loading) return <PendingSpinner />

  if (accessQuery.error) {
    throw exceptionFromApolloError(accessQuery.error)
  } else if (projectAccessQuery.error) {
    throw exceptionFromApolloError(projectAccessQuery.error)
  }

  if (!hasProjectAccess) {
    const noAccessMsg = t("error.NO_USER_ACCESS", { email: signedInUser, ns: "common" })
    return <Oops heading={t("error.heading.NO_USER_ACCESS", { ns: "common" })} body={noAccessMsg} isAuthError={true} />
  }

  if (needsLicense) {
    errorStatus = 1
  }

  if (errorStatus === 1) {
    return (
      <MissingLicense
        heading={t("licenseErrorMessage.heading", { ns: "common" })}
        body1={t("licenseErrorMessage.body1", { ns: "common" })}
        body2={t("licenseErrorMessage.body2", { ns: "common" })}
        moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
        quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
        info={signedInUser}
        andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
        subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
      ></MissingLicense>
    )
  } else if (errorStatus > 0 && rootError) {
    return <OopsLocalError error={rootError} heading={t("error.fetchError")} />
  }

  if (loadingStatus) return <PendingSpinner />
  // -------------------------------------------------------------- Loading finished ---------------------------------------------------------------------------------

  const tasks = isRoot ? rootData.getProjectRootTasks : rootData.getTasks.length ? rootData.getTasks[0].subTasks : []
  const sortedFilteredList = tasks
    .map(function (item: any) {
      let temp = {
        taskId: item.id,
        icon: <img src={iconBindings(item.type)} width="24" height="24" alt="a" />,
        taskName: item.name,
        guid: item.guid,
        taskTag: item.type,
        subTaskIds: item.subTaskIds,
        subTasksCount: item.subTaskIds?.length ?? 0,
        createdTime: item.createdTime,
        latestModderName: userFullName(users.contents[item.lastModifiedBy]),
        latestDateMod: formatDateAsConnect(item.lastModifiedTime),
        latestModderId: users.contents[item.lastModifiedBy]?.id,
        deleted: item.deleted
      }
      if (item.deleted) {
        temp = {
          ...temp,
          icon: (
            <span style={{ position: "relative" }}>
              <img
                src={deleted_icon}
                alt="deleted"
                width="20"
                height="20"
                style={{ position: "absolute", top: "-10px", left: "-15px" }}
              />
              <img src={iconBindings(item.type)} width="24" height="24" alt="a" />
            </span>
          )
        }
      }
      return temp
    })
    .filter(function (item: any) {
      return filterCheck(item, filter)
    })
    .sort((a: any, b: any) =>
      sortConfig.type ? b.taskName.localeCompare(a.taskName) : a.taskName.localeCompare(b.taskName)
    )

  const onRowClick = (index: number) => {
    const task = sortedFilteredList[index]
    const taskUnChanged = task.taskName === prevTask?.taskName
    setSelectedTask(
      // Switches the sidebar on if it is off, and the user clicks on a task in the main grid.
      // Switches the sidebar off if it is on, and the user clicks on the already selected task in the main grid
      // Lets the sidebar remain on, if the user clicks on a currently un-selected task in the main grid. Then also switches contents to show the new tasks' subtasks
      taskUnChanged && sidebarToggle
        ? null
        : {
            taskName: task.taskName,
            guid: task.guid,
            taskId: task.taskId,
            taskTag: task.taskTag,
            subTaskIds: task.subTaskIds,
            clickedTaskId: -1,
            createdData: makeDatePersonEntry(task.createdTime, "", cBy, cNone),
            lastModifiedData: makeDatePersonEntry(task.latestDateMod, task.latestModderName, cBy, cNone),
            includeDeleted: filter.includeDeleted
          }
    )
    setTaskRowSelected([index])
    entryPosition = index // Set to correct position when user clicks on a task in the main grid
    setPrevTask(task)
  }
  // If user clicked on a task in the sidebar (causing subSub.length > 0), the sidebar task list is "moved" to the main grid. Go through the main grid list to re-locate and mark it
  for (let iCount = 0; iCount < sortedFilteredList.length && subSub.length; iCount++) {
    if (sortedFilteredList[iCount].taskId === sidebarToggle?.taskId) {
      entryPosition = iCount // Set to correct position when user clicks on a task in the side panel
      break
    }
  }

  const visibleTaskTypes: string[] = unique<string>(tasks.map((task: Task) => task.type ?? "NULL")).filter(
    (x) => x !== null
  )

  const onRowDbClick = (index: number) => {
    const task = sortedFilteredList[index]
    const changeTo = "/tasks/" + task.taskId
    setSelectedTask(null)
    history.push(changeTo)
  }

  const invalidateDetailsPane = () => {
    setTaskRowSelected([])
    setSelectedTask(null)
  }

  const usersApiUrl = `${projectOrigin}/projects/${projectId}/users?includesRemoved=false`

  return (
    <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
      <div className="head-container">
        <div className="page-heading">
          <TasksBreadCrumbs rootData={rootData} />
          <ProjectFeatureCatalogLink quadriConfigurationUiUrl={quadriConfigurationUiUrl} />
        </div>
      </div>

      <div className="toolbar-section no-gap">
        <div className="w-100">
          <Filter
            {...{
              filter,
              onSearchEnter,
              onFilterChange,
              usersApiUrl,
              resetFilter,
              getTasksFromAPI,
              handleToggle,
              handleVisualizationClick,
              showChart,
              projectName,
              ownershipName,
              chartData: chartData,
              visibleTasks: visibleTaskTypes,
              taskTypes: taskTypes
            }}
          />
        </div>
      </div>
      {sortedFilteredList.length ? (
        <div className="row">
          <div className="col-12 col-md-12">
            <CommonTable<Task>
              columns={columnsSet}
              sortOptions={sortConfig}
              lazyLoad={false}
              loadMore={async () => null}
              list={sortedFilteredList}
              isLoading={false}
              onRowSelect={onRowClick}
              onRowDbClick={onRowDbClick}
              selectedRows={[entryPosition]}
              doSort={onSort}
            />
          </div>
        </div>
      ) : filterIsActive(filter) || !filter.showDeletedTask ? (
        <div className="row">
          <div className="col-12 col-md-12">
            <NoData
              icon="tc-icon-libraries"
              heading={t("runtimeTexts.everythingFilteredOutHeading")}
              message={t("runtimeTexts.everythingFilteredOutMessage")}
            />
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12 col-md-12">
            <NoData
              icon="tc-icon-libraries"
              heading={t("runtimeTexts.noTasks")}
              message={t("runtimeTexts.doCreateTasks")}
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface filterProps {
  onFilterChange: (event: Event, which: string) => void
  usersApiUrl: string
  filter: TasksFilter
  resetFilter: () => void
  onSearchEnter: (e: any) => void
  getTasksFromAPI: boolean
  handleToggle: () => void
  handleVisualizationClick: () => void
  showChart: boolean
  projectName?: string
  ownershipName?: string
  chartData: (string | number)[][]
  visibleTasks: string[]
  taskTypes: TaskTypeDefinition[]
}

const Filter = (props: filterProps) => {
  const { t } = useTranslation(["tasks", "common"])
  const [filterSidebar, setFilterSidebar] = useState(false)
  const handleFilterSidebar = () => {
    setFilterSidebar(!filterSidebar)
  }
  const {
    onFilterChange,
    usersApiUrl,
    filter,
    resetFilter,
    onSearchEnter,
    getTasksFromAPI,
    handleToggle,
    handleVisualizationClick
  } = props

  return (
    <div className="row">
      <div className="d-none d-lg-flex col-lg-8 col-xl-9 filters">
        <div>
          <div className="filter-type">
            <UserPicker
              id="tasks-user-picker"
              onChange={onFilterChange}
              apiUrl={usersApiUrl}
              selectionList={filter.connectUserIds || []}
            />
          </div>
          <div className="filter-type">
            <DateRangePicker
              onChange={onFilterChange}
              id="tasks-date-range-picker"
              selection={{
                from: filter.startDate || null,
                to: filter.endDate || null
              }}
            />
          </div>
          <DropDownPicker
            title="Type"
            options={props.taskTypes.map((type) => ({
              title: type.name,
              key: type.id,
              checked: filter.types
            }))}
            visible={props.visibleTasks}
            selected={filter.types}
            onChange={(types: string[]) =>
              onFilterChange(
                new CustomEvent("type-picker", { detail: { data: types.map((x) => ({ value: x })) } }),
                "type-picker"
              )
            }
          />
          <div className="filter-type">
            <Checkbox
              handleChange={(e: any) => onFilterChange(e, "show-deleted")}
              isChecked={filter.includeDeleted}
              label={t("showRemovedTasks")}
              disabled={getTasksFromAPI}
            />
          </div>
          {filterIsActive(filter) && (
            <button className="button link-primary" id="filter-reset" onClick={resetFilter}>
              {t("reset", { ns: "common" })}
            </button>
          )}

          {GetEnvironment().featureFlagUseRestApi && (
            <div className="d-flex align-items-center ">
              <Checkbox
                handleChange={handleToggle}
                isChecked={getTasksFromAPI}
                label={t("useRestApi")}
                disabled={filter.includeDeleted}
              />
              <span className="ml-2">
                <img
                  src={dashboard_icon}
                  alt="visualization"
                  width="30"
                  height="20"
                  style={{ cursor: "pointer" }}
                  onClick={handleVisualizationClick}
                  title={t("visualization.tooltipText")}
                />
              </span>
            </div>
          )}
          {props.showChart && (
            <ChartDashboard
              data={props.chartData}
              projectName={props.projectName}
              ownershipName={props.ownershipName}
              handleVisualizationClick={handleVisualizationClick}
            />
          )}
        </div>
      </div>
      <div className="col-9 col-md-6 col-lg-4 col-xl-3">
        <div className="input-button-group search">
          <button className="button default hollow icon-medium icon-circle">
            <i className="icon-font tc-icon-search"></i>
          </button>
          <input type="text" placeholder="Search for task name..." onChange={onSearchEnter} value={filter.nameSearch} />
          <div className="line"></div>
        </div>
      </div>
      <div className="filterIcon d-flex flex-row-reverse d-lg-none col">
        <FilterIcon handleFilterSidebar={handleFilterSidebar}></FilterIcon>
      </div>
      {/* sidebar for filter (sm to md screens) */}
      <FilterSidebar filterSidebar={filterSidebar} handleFilterSidebar={handleFilterSidebar} resetFilter={resetFilter}>
        <UserPicker
          id="tasks-user-picker"
          onChange={onFilterChange}
          apiUrl={usersApiUrl}
          selectionList={filter.connectUserIds || []}
        />
        <DateRangePicker
          onChange={onFilterChange}
          id="tasks-date-range-picker"
          selection={{
            from: filter.startDate || null,
            to: filter.endDate || null
          }}
        />
        <Checkbox
          handleChange={(e: any) => onFilterChange(e, "show-deleted")}
          isChecked={filter.includeDeleted}
          label={t("showRemovedTasks")}
        />
      </FilterSidebar>
    </div>
  )
}

function filterCheck(entry: Task, theFilter: TasksFilter) {
  const latestChangeDate = new Date(entry.latestDateMod)
  const fromDate = new Date(theFilter.startDate ?? "1990-01-01")
  const toDate = new Date(theFilter.endDate ?? "2990-01-01")
  if (fromDate > latestChangeDate || toDate < latestChangeDate) {
    return false
  }

  if (theFilter.connectUserIds?.length) {
    if (!theFilter.connectUserIds?.find((uId) => uId === entry.latestModderId)) return false
  }

  if (theFilter.types?.length) {
    if (theFilter.types?.indexOf(entry.taskTag ?? "NULL") === -1) return false
  }
  const fLen = (theFilter.nameSearch ?? "").length

  if (!fLen) {
    // Filterstring not set?
    return true
  }

  if (fLen > entry.taskName.length) {
    // Filterstring longer than entry?
    return false
  }

  if (
    theFilter.nameSearch &&
    entry.taskName.toLocaleUpperCase().indexOf(theFilter.nameSearch.toLocaleUpperCase()) >= 0
  ) {
    // Case-insensitive comparison between filterstring and entry found a match?
    return true
  }

  return false
}

const datePickerDefaultValue = {
  from: null,
  to: null
}

const getDefaultFilter = () => ({
  nameSearch: "",
  connectUserIds: [],
  includeDeleted: false,
  types: []
})

const filterIsActive = (filter: TasksFilter): boolean =>
  (filter.nameSearch && filter.nameSearch.length > 0) ||
  (filter.connectUserIds && filter.connectUserIds.length > 0) ||
  typeof filter.startDate !== "undefined" ||
  typeof filter.endDate !== "undefined" ||
  filter.includeDeleted

export default TasksMain
export { makeDatePersonEntry }
